import { useMemo, useEffect } from "react";
import {
  Car,
  Wrench,
  Truck,
  CreditCard,
  Plus,
  Send,
  ReceiptText,
} from "lucide-react";
import { useDashboardCache } from "./hooks/useDashboardCache";
import { useDashboardPrefs } from "./hooks/useDashboardPrefs";
import { DashboardHeader } from "./components/DashboardHeader";
import { QuickActionsRow } from "./components/QuickActionsRow";
import { KpiCard, KpiCardSkeleton } from "./components/KpiCard";
import { NeedsAttention } from "./components/NeedsAttention";
import { RevenueChart } from "./components/RevenueChart";
import { ActiveJobsTable } from "./components/ActiveJobsTable";
import { TodaysAppointmentsStrip } from "./components/TodaysAppointmentsStrip";
import { money } from "@/lib/api";

const inr = (n) => `₹${money(n || 0)}`;

/**
 * OwnerDashboard — full spec for Garage Owner + Branch Manager.
 */
export default function OwnerDashboard({ user }) {
  const [prefs, patchPrefs] = useDashboardPrefs();

  // Params shared across all queries
  const params = useMemo(() => {
    const p = {};
    if (prefs.range === "custom" && prefs.from_date && prefs.to_date) {
      p.range = "custom";
      p.from_date = prefs.from_date;
      p.to_date = prefs.to_date;
    } else {
      p.range = prefs.range || "today";
    }
    return p;
  }, [prefs]);

  const summary = useDashboardCache("fender_dash_summary", "/dashboard/summary", { params });
  const revenue = useDashboardCache("fender_dash_revenue", "/dashboard/revenue-trend", {
    params: { days: 60 },
  });
  const jobs = useDashboardCache("fender_dash_jobs", "/dashboard/active-jobs", {
    params: { limit: 8 },
  });
  const attention = useDashboardCache("fender_dash_attention", "/dashboard/action-centre");

  const loading = summary.loading || revenue.loading || jobs.loading;

  const reloadAll = () => {
    summary.reload();
    revenue.reload();
    jobs.reload();
    attention.reload();
  };

  useEffect(() => {
    const onOnline = () => reloadAll();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
     
  }, []);

  // Build the KPI catalogue (mapping key → data).
  const s = summary.data || {};
  const catalog = {
    vehicles_in_workshop: {
      key: "vehicles_in_workshop",
      label: "Vehicles in Workshop",
      value: s.vehicles_in_workshop ?? "—",
      hint: `${s.received_today ?? 0} received today`,
      Icon: Car,
      tone: "frosted",
      to: "/job-cards?status=in_progress",
    },
    jobs_in_progress: {
      key: "jobs_in_progress",
      label: "Jobs in Progress",
      value: s.jobs_in_progress ?? "—",
      hint: `${s.due_today ?? 0} due today`,
      Icon: Wrench,
      tone: "steel",
      to: "/job-cards?status=in_progress",
    },
    ready_for_delivery: {
      key: "ready_for_delivery",
      label: "Ready for Delivery",
      value: s.ready_for_delivery ?? "—",
      hint: `${s.ready_pending_payment ?? 0} payment${(s.ready_pending_payment ?? 0) === 1 ? "" : "s"} pending`,
      Icon: Truck,
      tone: "green",
      to: "/job-cards?status=completed",
    },
    payments_pending: {
      key: "payments_pending",
      label: "Payments Pending",
      value: inr(s.outstanding_total),
      hint: `${s.outstanding_count ?? 0} invoice${(s.outstanding_count ?? 0) === 1 ? "" : "s"}`,
      Icon: CreditCard,
      tone: "red",
      to: "/invoices",
    },
  };

  const visibleKpis = prefs.kpi_order.filter((k) => !prefs.kpi_hidden.includes(k));

  const quickActions = [
    { key: "create-jc", label: "Create Job Card", Icon: Plus, url: "/job-cards/new", tone: "primary" },
    { key: "create-est", label: "Create Estimate", Icon: ReceiptText, url: "/estimates/new" },
    { key: "record-payment", label: "Record Payment", Icon: CreditCard, url: "/invoices" },
    { key: "submit-enquiry", label: "Submit Parts Enquiry", Icon: Send, url: "/enquiries", tone: "frosted" },
  ];

  return (
    <div className="space-y-6" data-testid="owner-dashboard-v3">
      <DashboardHeader
        title="Workshop Dashboard"
        range={prefs.range}
        fromDate={prefs.from_date}
        toDate={prefs.to_date}
        onRangeChange={(k) => patchPrefs({ range: k, from_date: null, to_date: null })}
        onCustomRange={(from, to) => patchPrefs({ range: "custom", from_date: from, to_date: to })}
        onRefresh={reloadAll}
        lastSyncedAt={summary.lastSyncedAt}
        loading={loading}
      />

      {/* Quick actions */}
      <QuickActionsRow actions={quickActions} />

      {/* KPI cards — up to 6 */}
      <div
        className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3"
        data-testid="kpi-grid"
      >
        {visibleKpis.map((key) => {
          const kpi = catalog[key];
          if (!kpi) return null;
          if (summary.loading && !summary.data) {
            return <KpiCardSkeleton key={key} testId={`kpi-${key}-skeleton`} />;
          }
          const { key: _k, ...kpiProps } = kpi;
          return <KpiCard key={key} testId={`kpi-${key}`} {...kpiProps} />;
        })}
      </div>

      {/* Revenue trend — 60-day series, sliced by header range */}
      <RevenueChart
        series={revenue.data?.series || []}
        range={prefs.range || "today"}
        days={revenue.data?.days || 60}
        loading={revenue.loading && !revenue.data}
      />

      {/* Active Jobs Table (now shown before Needs Attention) */}
      <ActiveJobsTable rows={jobs.data?.rows || []} />

      {/* Today's Appointments strip */}
      <TodaysAppointmentsStrip />

      {/* Needs Attention (moved below Active Jobs per operator feedback) */}
      <NeedsAttention
        items={attention.data?.items || []}
        onViewAll={() => (window.location.href = "/notifications")}
      />

      {/* Error banner if data could not refresh but cache exists */}
      {summary.error && summary.data && (
        <div
          className="rounded-lg border border-[#F79009]/30 bg-[#F79009]/10 px-4 py-2.5 text-[12.5px] text-[#7c4a02]"
          data-testid="dashboard-stale-warning"
        >
          Dashboard could not refresh — showing data from{" "}
          {summary.lastSyncedAt
            ? new Date(summary.lastSyncedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
            : "your last session"}
          .{" "}
          <button onClick={reloadAll} className="underline font-semibold" data-testid="dashboard-retry">
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
